

import React from 'react';
import { UserProfile, UserTier, StarType } from '../types';
import { starIconsMap } from './StarPicker'; // Import to use icons and labels

interface UserProfilePageProps {
  profile: UserProfile | null;
  currentTier: UserTier;
  onTierChange: (newTier: UserTier) => void;
  onNavigateBack: () => void; 
  onNavigateToStarredImages: (starType: StarType, starLabel: string) => void;
}

const UserProfilePage: React.FC<UserProfilePageProps> = ({
  profile,
  currentTier,
  onTierChange,
  onNavigateBack,
  onNavigateToStarredImages,
}) => {
  if (!profile) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-gray-800 text-gray-100">
        <p className="text-xl">No se pudo cargar el perfil del usuario.</p>
        <button
          onClick={onNavigateBack}
          className="mt-6 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg shadow-md"
        >
          Volver
        </button>
      </div>
    );
  }

  const handleTierSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onTierChange(event.target.value as UserTier);
  };

  const formattedRegistrationDate = new Date(profile.registrationDate).toLocaleDateString('es-ES', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  const availableStarTypesForFilter = Object.values(StarType).filter(st => st !== StarType.None);

  return (
    <div className="flex-1 flex flex-col items-center justify-start p-4 sm:p-8 bg-gray-850 text-gray-100 overflow-y-auto">
      <div className="w-full max-w-2xl bg-gray-800 shadow-2xl rounded-lg p-6 md:p-8">
        <h1 className="text-3xl font-bold text-indigo-300 mb-6 text-center">
          Perfil de Usuario
        </h1>

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-400">Nombre de Usuario</label>
            <p className="mt-1 text-lg text-gray-100 bg-gray-700 p-2 rounded-md">{profile.username}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">Email</label>
            <p className="mt-1 text-lg text-gray-100 bg-gray-700 p-2 rounded-md">{profile.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">Fecha de Registro</label>
            <p className="mt-1 text-lg text-gray-100 bg-gray-700 p-2 rounded-md">{formattedRegistrationDate}</p>
          </div>
          <div>
            <label htmlFor="userTier" className="block text-sm font-medium text-gray-400">
              Nivel de Suscripción (para desarrollo)
            </label>
            <select
              id="userTier"
              value={currentTier}
              onChange={handleTierSelectChange}
              className="mt-1 block w-full p-2.5 border border-gray-600 bg-gray-700 text-gray-100 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-lg"
            >
              {Object.values(UserTier).map(tierValue => (
                <option key={tierValue} value={tierValue}>
                  {tierValue}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Este selector cambia el nivel de usuario para pruebas de desarrollo.</p>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-gray-700">
          <h2 className="text-2xl font-semibold text-yellow-400 mb-4 text-center">Imágenes Favoritas (por Marca)</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3"> {/* Adjusted grid columns */}
            {availableStarTypesForFilter.map(starType => {
              const starInfo = starIconsMap[starType];
              if (!starInfo) return null;
              const IconComponent = starInfo.icon;
              return (
                <button
                  key={starType}
                  onClick={() => onNavigateToStarredImages(starType, starInfo.label)}
                  className={`flex flex-col items-center justify-center p-3 rounded-md transition-colors duration-150 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-800 ${starInfo.color.replace('hover:', 'hover:bg-opacity-20 ')} bg-gray-700 hover:bg-gray-600`}
                  title={`Ver imágenes marcadas con ${starInfo.label}`}
                >
                  <IconComponent className={`w-7 h-7 mb-1.5 ${starInfo.color.split(' ')[0]}`} />
                  <span className="text-xs text-center text-gray-200">
                    {starInfo.label}
                  </span>
                </button>
              );
            })}
          </div>
           <p className="text-xs text-gray-500 mt-3 text-center">Haz clic en una marca para ver todas las imágenes que has marcado con ella.</p>
        </div>

        <div className="mt-10 text-center">
          <button
            onClick={onNavigateBack}
            className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg shadow-md transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 focus:ring-offset-gray-800"
          >
            Volver al Estudio
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
